import { useContext, memo, useState, useEffect } from "react";
import {
  Flex,
  Text,
  Box,
  Button,
  ActionIcon,
  useMantineTheme,
  List,
  Stack,
  Loader,
} from "@mantine/core";
import {
  CatalogContext,
  IProductCart,
  UnsavedMethod,
  useCatalogContext,
} from "src/api/context/CatalogContext";
import { IoIosArrowRoundBack } from "react-icons/io";
import { ShoppingCartMethods, formatter } from "src/utils/constants";
import Customer from "./Customer";
import Payment from "./Payment";
import Discount from "./Discount";
import { useModals } from "@mantine/modals";
import PayrocHostedTransaction from "../hostedTransaction";
import {
  useCreateShoppingCartMutation,
  useUpdateShoppingCartMutation,
  useCreateTransactionMutation,
  ShoppingCart,
  CreateShoppingCartMutation,
  GenericConnectInput,
} from "src/generated/graphql";
import { AppContext } from "src/api/AppContext";
import { currencyFormatter, currencyRoundFloat } from "src/utils/formatting";
import PendingPayByCloud from "./PendingPayByCloud";

export type ProductSummary = {
  products: IProductCart[];
  subTotal: number;
};

export type Cart = CreateShoppingCartMutation["createShoppingCart"];

type TaxObj = {
  id: string;
  name: string;
  amount: number;
};

type DiscountObj = {
  id: string;
  name: string;
  isPercent: boolean;
  isCartwide: boolean;
  amount: number;
};

const defaultProductSummary = {
  products: [] as IProductCart[],
  subTotal: 0,
  tax: 0,
  total: 0,
  discounts: 0,
  groupedTaxes: {} as Record<string, number>,
};
function Checkout() {
  const theme = useMantineTheme();
  const modals = useModals();
  let color = theme.colors.secondaryTextColor[6];
  const borderBottom = `1px solid ${theme.colors.secondary[6]}`;
  const {
    handleBack,
    appliedCodes,
    productsCart,
    selectedUnsavedMethod,
    selectedTerminalId,
    selectedCustomer,
    selectedPayment,
    resetState,
  } = useContext(CatalogContext);
  const { currentEmployee, currentLocation, currentOrganization } =
    useContext(AppContext);
  const [createShoppingCart] = useCreateShoppingCartMutation();
  const [updateShoppingCart] = useUpdateShoppingCartMutation();

  const [cart, setCart] = useState<Cart | undefined>(undefined);

  const [createTransaction] = useCreateTransactionMutation();

  const [productSummary, setproductSummary] = useState<ProductSummary>(
    defaultProductSummary
  );
  const [isPaymentSuccessful, setIsPaymentSuccessful] =
    useState<boolean>(false);

  useEffect(() => {
    const products = productsCart.flatMap((item) => {
      let replica = { ...item };
      if (replica.isPostTax && replica.isTaxable) {
        let totalTaxRate = 0;
        replica.taxRates?.forEach(
          (tax) => (totalTaxRate = totalTaxRate + tax.taxRate)
        );
        const taxIncluded = replica.cost * (totalTaxRate / 100);
        replica.cost = replica.cost - taxIncluded;
      }
      return replica;
    });

    let subTotal = products.reduce(
      (sum, obj) => sum + (obj.count || 0) * obj.cost,
      0
    );

    setproductSummary({
      products,
      subTotal: currencyRoundFloat(subTotal),
    });
  }, [productsCart]);

  const createCart = async () => {
    const shoppingCart = await createShoppingCart({
      variables: {
        data: {
          ...(selectedCustomer
            ? { client: { id: selectedCustomer.client?.id } }
            : {}),
          employee: currentEmployee ? { id: currentEmployee.id } : undefined,
          products: productSummary.products.map((e) => ({
            product: { id: e.id },
            count: e.count,
          })),
          discountCodes: appliedCodes.map(
            (code) => ({ id: code.id } as GenericConnectInput)
          ),
          location: { id: currentLocation!.id },
          organization: { id: currentOrganization!.id },
        },
      },
    });
    if (shoppingCart.data?.createShoppingCart?.id) {
      ShoppingCartMethods.set(shoppingCart.data?.createShoppingCart?.id);
      setCart(shoppingCart.data.createShoppingCart);
    }
  };

  const updateCart = async (cartId: string) => {
    let shoppingCart = await updateShoppingCart({
      variables: {
        id: cartId,
        data: {
          ...(selectedCustomer
            ? { client: { id: selectedCustomer.client?.id } }
            : {}),
          employee: currentEmployee ? { id: currentEmployee.id } : undefined,
          products: productSummary.products.map((e) => ({
            product: { id: e.id },
            count: e.count,
          })),
          discountCodes: appliedCodes.map(
            (code) => ({ id: code.id } as GenericConnectInput)
          ),
        },
      },
    });
    if (shoppingCart.data?.updateShoppingCart?.id) {
      setCart(shoppingCart.data.updateShoppingCart);
    }
  };

  useEffect(() => {
    if (
      productSummary?.products.length > 0 &&
      currentLocation &&
      currentOrganization
    ) {
      let cartId = ShoppingCartMethods.get();
      if (cartId) {
        updateCart(cartId);
      } else {
        createCart();
      }
    }
  }, [
    selectedCustomer,
    productSummary,
    appliedCodes,
    currentLocation,
    currentOrganization,
  ]);

  const startTransaction = async () => {
    //check for a pay by cloud option here
    if (
      selectedUnsavedMethod === UnsavedMethod.Terminal &&
      selectedTerminalId !== ""
    ) {
      let shoppingCartId = ShoppingCartMethods.get();
      if (!shoppingCartId) {
        console.error("Unable to retrieve cart!");
        return;
      }

      if (!currentLocation) {
        console.error("Location access failure.");
        return;
      }

      //generate transaction/payment instructions
      let transaction = await createTransaction({
        variables: {
          data: {
            performedBy: currentEmployee
              ? { id: currentEmployee.id }
              : undefined,
            location: { id: currentLocation.id },
            isRecurring: false,
            targetClient: selectedCustomer
              ? { id: selectedCustomer?.client.id }
              : undefined,
            paymentTerminal: { id: selectedTerminalId },
            shoppingCart: { id: shoppingCartId },
          },
        },
      });

      if (transaction.data?.createTransaction) {
        modals.openModal({
          onClose: () => {
            if (isPaymentSuccessful) {
              setIsPaymentSuccessful(false);
              resetState && resetState();
            }
          },
          closeOnClickOutside: false,
          title: "Secure Transaction",
          children: (
            <PendingPayByCloud
              transactionId={transaction.data?.createTransaction.id}
              onSuccess={() => setIsPaymentSuccessful(true)}
            />
          ),
          styles: {
            root: {
              marginTop: 50,
            },
            modal: {
              backgroundColor: "rgba(0,0,0,0) !important",
            },
            header: {
              backgroundColor: theme.colors.primary[6],
              color: theme.colors.primaryTextColor[6],
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            },
            body: {
              color: theme.colors.secondaryTextColor[6],
              backgroundColor: theme.colors.secondary[6],
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
            },
          },
        });
      }
    } else {
      if (cart) {
        modals.openModal({
          modalId: "secureTransactionModal",
          closeOnClickOutside: false,
          title: "Secure Transaction",
          children: (
            <PayrocHostedTransaction
              cart={cart}
              shoppingCartId={ShoppingCartMethods.get() || ""}
            />
          ),
          styles: {
            modal: {
              backgroundColor: "rgba(0,0,0,0) !important",
            },
            header: {
              backgroundColor: theme.colors.primary[6],
              color: theme.colors.primaryTextColor[6],
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            },
            body: {
              color:
                selectedUnsavedMethod === UnsavedMethod.Cash
                  ? theme.colors.secondaryTextColor[6]
                  : "#222222 !important",
              backgroundColor:
                selectedUnsavedMethod === UnsavedMethod.Cash
                  ? theme.colors.secondary[6]
                  : "#f0f0f0",
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
            },
          },
        });
      }
    }
  };

  return (
    <Box
      mx={"md"}
      my={0}
      sx={{
        height: "100% !important",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flex gap={"sm"} align={"center"} sx={{ borderBottom }} p={3}>
        <ActionIcon
          onClick={handleBack}
          size={"lg"}
          sx={{
            "&:hover": {
              backgroundColor: theme.colors.secondary[6],
            },
          }}
        >
          <IoIosArrowRoundBack color={color} size={27} />
        </ActionIcon>

        <Text size={24}>Back to Catalog</Text>
      </Flex>

      <Box mt={"xs"}>
        <Discount />
      </Box>

      {!cart ? (
        <Loader />
      ) : (
        <Box mt={"md"} style={{}}>
          <Flex justify={"space-between"} sx={{ borderBottom }} pb={6}>
            <Text size={18} fw={500}>
              Items
            </Text>
          </Flex>

          <List
            styles={{
              itemWrapper: {
                width: "100%",
              },
              item: {
                listStyle: "none",
                borderBottom,
                padding: "4px 0",
                margin: " 2px 10px",
                color: theme.colors.secondaryTextColor[6],
              },
            }}
          >
            <List.Item>
              <Flex align={"center"} justify={"space-between"}>
                <Text size={13} fw={600} sx={{ width: "20%" }}>
                  Item
                </Text>
                <Text size={13} fw={600}>
                  Count
                </Text>
                <Text size={13} fw={600}>
                  Total
                </Text>
              </Flex>
            </List.Item>

            {cart.lineItems.map((lineItem) => {
              let discounts = JSON.parse(
                lineItem.discounts || "[]"
              ) as DiscountObj[];
              return (
                <List.Item key={lineItem.id}>
                  <Stack>
                    <Flex align={"center"} justify={"space-between"}>
                      <Text size={14} sx={{ width: "20%" }}>
                        {lineItem.product?.name}
                      </Text>
                      <Text size={14}>{lineItem.count}</Text>
                      <Text size={14}>
                        {formatter.format(lineItem.subtotal || 0)}
                      </Text>
                    </Flex>

                    {discounts
                      .filter((disc) => !disc.isCartwide)
                      .map((disc) => {
                        return (
                          <Flex align={"center"} justify={"space-between"}>
                            <Text
                              size={14}
                              sx={{ width: "18%", marginLeft: "2%" }}
                            >
                              {disc.name}
                            </Text>
                            <Text size={14}>{}</Text>
                            <Text size={14}>
                              {formatter.format(-1 * disc.amount)}
                            </Text>
                          </Flex>
                        );
                      })}
                  </Stack>
                </List.Item>
              );
            })}

            {(JSON.parse(cart.discounts || "[]") as DiscountObj[]).filter(
              (disc) => disc.isCartwide
            ).length > 0 && (
              <List.Item>
                <Flex align={"center"} justify={"space-between"}>
                  <Text size={14} fw={600} sx={{ width: "20%" }}>
                    Discounts
                  </Text>
                  <Stack>
                    {(JSON.parse(cart.discounts || "[]") as DiscountObj[])
                      .filter((disc) => disc.isCartwide)
                      .map((disc) => (
                        <Text size={14} fw={600}>{`${
                          disc.name
                        }: ${formatter.format(-1 * disc.amount)}`}</Text>
                      ))}
                  </Stack>
                </Flex>
              </List.Item>
            )}

            <List.Item>
              <Flex align={"center"} justify={"space-between"}>
                <Text size={14} fw={600} sx={{ width: "20%" }}>
                  Sub Total
                </Text>
                <Text size={14} fw={600}>
                  {formatter.format(
                    (cart.subtotal || 0) - (cart.discount || 0)
                  )}
                </Text>
              </Flex>
            </List.Item>

            {cart.taxes &&
              (JSON.parse(cart.taxes) as TaxObj[]).map((tax) => (
                <List.Item key={tax.id}>
                  <Flex align={"center"} justify={"space-between"}>
                    <Text size={14} sx={{ width: "20%" }}>
                      {tax.name}
                    </Text>
                    <Text size={14}>{formatter.format(tax.amount)}</Text>
                  </Flex>
                </List.Item>
              ))}

            <List.Item>
              <Flex align={"center"} justify={"space-between"}>
                <Text size={14} fw={600} sx={{ width: "20%" }}>
                  Taxes
                </Text>
                <Text size={14} fw={600}>
                  {formatter.format(cart.tax || 0)}
                </Text>
              </Flex>
            </List.Item>

            <List.Item>
              <Flex align={"center"} justify={"space-between"}>
                <Text size={14} fw={600} sx={{ width: "20%" }}>
                  Total
                </Text>
                <Text size={14} fw={600}>
                  {formatter.format(cart.total || 0)}
                </Text>
              </Flex>
            </List.Item>
          </List>
        </Box>
      )}

      <Box mt={"md"} style={{ flex: 1 }}>
        <Box mt={"md"}>
          <Customer />
        </Box>

        <Box mt={"md"}>{selectedCustomer && <Payment />}</Box>
      </Box>

      <Box mt={"md"} mb={"xs"}>
        <Text size={10}>
          All sales are final. Refunds are not guaranteed and, if applicable,
          will be resolved on a case-by-case basis. In the case of
          subscriptions, the itemized totals shown above represent only the
          initial charges due at sign-up and are not reflective of the total
          recurring payments due over the term duration.
        </Text>
        <Button
          mt={5}
          mb={"md"}
          fullWidth
          disabled={
            !selectedCustomer || (!selectedPayment && !selectedUnsavedMethod)
          }
          sx={{
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          onClick={() => {
            startTransaction();
          }}
        >
          Buy Now {formatter.format(cart?.total || 0)}
        </Button>
      </Box>
    </Box>
  );
}

export default memo(Checkout);
